import type { FunctionComponent } from 'react';
import { useContext, useEffect, useState } from 'react';

import { importMenuViaMenuManagement } from 'helpers/menuManagement';
import { formatMerchantID } from 'helpers/auth.cuboh';
import { formatNotificationTimestamp } from 'helpers/format';
import { logException } from 'helpers/loggers';

import { useCubohActionBar } from 'hooks/useCubohActionBar';
import { useMenuManagement } from 'hooks/useMenuManagement';
import useEffectEvent from 'hooks/useEffectEvent';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'i18n';

import { breakpoints } from 'styles/theme.variables';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import {
  Alert,
  Box,
  Button,
  Center,
  Drawer,
  Loader,
  LoadingOverlay,
  Radio,
  Stack,
  Text,
} from '@mantine/core';

import {
  MENU_NOTIFICATION_EVENT,
  MENU_NOTIFICATION_TYPE,
  SQUARE_APP_ID,
} from 'constants/menuManagement';

import MenuActions from '../MenuActions';

const NS = 'menuManagement';

export type ImportsInProgress = {
  [key: string]: string;
};

type IntegrationOption = {
  applicationID?: string;
  label: string;
  value: string;
};

type AvailableIntegration = {
  id?: string;
  integrationName: string;
  readableAppName: string;
};

type ImportMenuDrawerProps = {
  close: () => void;
  importsInProgress: ImportsInProgress;
  merchantId: string;
  opened: boolean;
};

export const ImportMenuDrawer: FunctionComponent<ImportMenuDrawerProps> = ({
  close,
  importsInProgress,
  merchantId,
  opened,
}) => {
  // useSuspense: false avoids suspending the page's fallback-less Suspense (CN-50159)
  const { t } = useTranslation(NS, { useSuspense: false });
  const { createListOfAvailableIntegrations } = useContext(MenuActions.Context);
  const { fetchMerchantIntegrations, getMerchantIntegrationsFromState } = useMenuManagement();

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const { singleSelectedCompanyID } = useCubohActionBar();

  const [radioOptions, setRadioOptions] = useState<IntegrationOption[] | undefined>();
  const [value, setValue] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<IntegrationOption | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const onOpen = useEffectEvent(() => {
    fetchMerchantIntegrations(singleSelectedCompanyID, merchantId);
  });

  useEffect(() => {
    if (opened) onOpen();
  }, [opened, onOpen]);

  const loadIntegrationOptions = useEffectEvent(async () => {
    const activeMerchantIntegrations = getMerchantIntegrationsFromState(merchantId);
    if (activeMerchantIntegrations) {
      const integrations: AvailableIntegration[] =
        await createListOfAvailableIntegrations(merchantId);
      const radioButtonOptions = integrations.map(integration => ({
        applicationID: integration.id,
        label: integration.readableAppName,
        value: integration.integrationName,
      }));
      setRadioOptions(radioButtonOptions);
    }
  });

  useEffect(() => {
    if (opened) void loadIntegrationOptions();
  }, [opened, merchantId, loadIntegrationOptions]);

  const resetDefault = () => {
    setValue(null);
    setSelectedApp(undefined);
  };

  const handleOk = async () => {
    setIsLoading(true);
    try {
      const isSquare = value === SQUARE_APP_ID;
      const menuPullData = {
        companyId: singleSelectedCompanyID,
        merchantId: formatMerchantID(merchantId),
        source: {
          app: value,
          id: isSquare ? null : selectedApp?.applicationID,
        },
      };
      await importMenuViaMenuManagement(menuPullData);

      window.dispatchEvent(
        new CustomEvent(MENU_NOTIFICATION_EVENT, {
          detail: {
            message: t('ImportMenuDrawer.successMessage', {
              appName: selectedApp?.label,
              timestamp: formatNotificationTimestamp(),
            }),
            type: MENU_NOTIFICATION_TYPE.SUCCESS,
          },
        }),
      );
    } catch (error) {
      logException({ message: 'Failed to import menu', error, context: null, tags: null });
      window.dispatchEvent(
        new CustomEvent(MENU_NOTIFICATION_EVENT, {
          detail: {
            message: t('ImportMenuDrawer.errorMessage', { appName: selectedApp?.label }),
            type: MENU_NOTIFICATION_TYPE.ERROR,
          },
        }),
      );
    } finally {
      setIsLoading(false);
      resetDefault();
      close();
    }
  };

  const onChange = (val: string) => {
    setValue(val);
    const selectedIntegrationOption = radioOptions?.find(option => option.value === val);
    setSelectedApp(selectedIntegrationOption);
  };

  const handleOnClose = () => {
    resetDefault();
  };

  return (
    <Drawer
      opened={opened}
      onClose={() => {
        close();
        handleOnClose();
      }}
      title={t('ImportMenuDrawer.title')}
      size={isMobile ? 'sm' : 'lg'}
      variant="withDock"
      styles={
        isMobile
          ? {
              header: {
                padding:
                  'var(--mantine-spacing-xl) var(--mantine-spacing-xl) var(--mantine-spacing-md)',
              },
            }
          : undefined
      }
    >
      <DockedDrawerFormContent
        size="lg"
        dockButtons={
          <Button disabled={!radioOptions?.length || !value} onClick={() => void handleOk()}>
            {t('ImportMenuDrawer.importButton')}
          </Button>
        }
      >
        <Box pt={{ base: 0, md: 'md' }}>
          <LoadingOverlay visible={isLoading} />
          {!radioOptions ? (
            <Center>
              <Loader />
            </Center>
          ) : (
            <>
              {!radioOptions?.length ? (
                <Alert variant="neutral">
                  <Text>{t('ImportMenuDrawer.noIntegrationsAvailable')}</Text>
                </Alert>
              ) : (
                <Radio.Group onChange={onChange} value={value}>
                  <Stack>
                    {radioOptions.map(option => (
                      <Radio
                        key={`${option.label}-integration`}
                        disabled={importsInProgress[option.value] === 'app'}
                        label={<Text variant="label-sm">{option.label}</Text>}
                        value={option.value}
                      />
                    ))}
                  </Stack>
                </Radio.Group>
              )}
            </>
          )}
        </Box>
      </DockedDrawerFormContent>
    </Drawer>
  );
};
